import { useState } from 'react';
import {
  Box, Grid, Stack, Typography, Switch, FormControlLabel, TextField, MenuItem,
  InputAdornment, Card, CardActionArea, Checkbox, Alert, Divider, Paper, Chip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { updateDph, toggleDphCharge } from '../../../../redux/pricingWizardSlice';
import { DPH_MECHANISMS, DPH_CHARGES } from '../constants';
import { computeDph } from '../helpers/dph';
import SectionTitle from './SectionTitle';

export default function DphFscCard() {
  const dispatch = useDispatch();
  const dph = useSelector((s) => s.pricingWizard.dph);
  const mech = DPH_MECHANISMS.find((m) => m.id === dph.mechanism) || DPH_MECHANISMS[0];

  // Live worked-example inputs (local only — not persisted).
  const [sampleD1, setSampleD1] = useState(99);
  const [sampleFreight, setSampleFreight] = useState(90);
  const calc = computeDph(dph, sampleD1, sampleFreight);

  return (
    <Box sx={{ mt: 1 }}>
      <Divider sx={{ my: 2.5 }} />
      <SectionTitle icon="⛽" title="DPH-Linked FSC" badge="Diesel Price Hike mechanism" />
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, mb: 1 }}>
        Captures the diesel-price-hike fuel surcharge for this agreement as structured fields, so the system can
        auto-compute DPH on invoices. Variance is always measured from <b>Base</b>; the monthly diesel price (D₁) is
        entered separately by Billing.
      </Typography>

      <FormControlLabel
        control={<Switch checked={dph.enabled} onChange={(e) => dispatch(updateDph({ enabled: e.target.checked }))} />}
        label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Enable DPH-Linked FSC for this agreement</Typography>}
      />

      {dph.enabled && (
        <>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth size="small" type="number" label="Base Diesel Price (₹/L)"
                value={dph.base_price ?? ''} onChange={(e) => dispatch(updateDph({ base_price: Number(e.target.value) || 0 }))}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                helperText="Reference price — DPH variance is always from Base."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth size="small" select label="Mechanism Type"
                value={dph.mechanism} onChange={(e) => dispatch(updateDph({ mechanism: e.target.value }))}
              >
                {DPH_MECHANISMS.map((m) => <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth size="small" type="number" label="Hike Rate" inputProps={{ step: 0.01 }}
                value={dph.hike_rate ?? ''} onChange={(e) => dispatch(updateDph({ hike_rate: Number(e.target.value) || 0 }))}
                helperText={`${mech.rateUnit} · ${mech.rateHint}`}
              />
            </Grid>
          </Grid>

          {/* Trigger Price — own row, toggle + value aligned inline */}
          <Box sx={{ mt: 1.5 }}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={<Switch size="small" checked={dph.trigger_enabled} onChange={(e) => dispatch(updateDph({ trigger_enabled: e.target.checked, trigger_price: e.target.checked ? (dph.trigger_price ?? '') : null }))} />}
              label={<Typography variant="caption" sx={{ fontWeight: 700 }}>Trigger Price (optional)</Typography>}
            />
            {dph.trigger_enabled ? (
              <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth size="small" type="number" label="Trigger (₹/L)"
                    value={dph.trigger_price ?? ''} onChange={(e) => dispatch(updateDph({ trigger_price: e.target.value === '' ? '' : Number(e.target.value) }))}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    helperText="DPH stays 0 until D₁ ≥ trigger, then variance from Base."
                  />
                </Grid>
              </Grid>
            ) : (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
                Off → DPH applies as soon as D₁ exceeds Base.
              </Typography>
            )}
          </Box>

          {/* Formula recap */}
          <Alert severity="info" icon={<span>📐</span>} sx={{ mt: 1.5, fontSize: 12 }}>
            <b>{mech.label}:</b> {mech.formula} · applied as {mech.appliedAs}.
          </Alert>

          {/* Applicable charges */}
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 2, mb: 0.75 }}>
            Applicable Charges <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>(DPH applies only to the checked components)</Typography>
          </Typography>
          <Grid container spacing={0.75}>
            {DPH_CHARGES.map((c) => {
              const on = !!dph.applicable[c.key];
              return (
                <Grid item xs={12} sm={6} md={4} key={c.key}>
                  <Card variant="outlined" sx={{ borderColor: on ? 'primary.main' : 'divider', bgcolor: on ? 'primary.lighter' : 'background.paper', height: '100%' }}>
                    <CardActionArea onClick={() => dispatch(toggleDphCharge(c.key))} sx={{ p: 1 }}>
                      <Stack direction="row" spacing={0.75} alignItems="flex-start">
                        <Checkbox size="small" checked={on} sx={{ p: 0.25, pointerEvents: 'none' }} />
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>{c.label}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: 10.5 }}>{c.note}</Typography>
                        </Box>
                      </Stack>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Live worked-example calculator */}
          <Paper variant="outlined" sx={{ mt: 2, p: 1.5, bgcolor: 'background.neutral' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>🧮 Worked example</Typography>
              <Chip size="small" label="read-only · for analyst confidence" sx={{ height: 18, fontSize: 10 }} />
            </Stack>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={6} sm={3}>
                <TextField fullWidth size="small" type="number" label="Sample D₁ (₹/L)" value={sampleD1}
                  onChange={(e) => setSampleD1(Number(e.target.value) || 0)}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth size="small" type="number" label="Sample freight (₹)" value={sampleFreight}
                  disabled={dph.mechanism === 'rupee'}
                  onChange={(e) => setSampleFreight(Number(e.target.value) || 0)}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  helperText={dph.mechanism === 'rupee' ? 'Rupee-linked DPH is flat — freight not used' : ''} />
              </Grid>
              <Grid item xs={12} sm={6}>
                {calc.active ? (
                  <Alert severity="success" sx={{ py: 0.5, fontSize: 12 }}>
                    DPH = <b>{dph.mechanism === 'percentage' ? `${calc.dphPct}%  →  ₹${calc.dphAmount}` : `₹${calc.dphAmount} flat`}</b>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{calc.reason}</Typography>
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ py: 0.5, fontSize: 12 }}>DPH = <b>₹0</b> · {calc.reason}</Alert>
                )}
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
    </Box>
  );
}
